// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase Orders LIST page (Stage 6P.2).
//
// Server-paginated list at /erp/purchase/orders.
//
// Columns (Section N):
//   PO No (link) · PO Date · Supplier (+ GSTIN tag) · Source PR
//   (link if set) · Expected Delivery · Status (Tag) · Grand Total
//   (₹) · Actions (⋯ menu).
//
// Toolbar: search, Status filter, Date range, "New".
// Pagination: 25 / 50 / 100.
//
// ADDITIVE-ONLY: This page only reads `purchaseOrdersApi` and the
// shared status / capability helpers from `./purchaseHelpers`. The
// PO detail / create / edit routes (`/erp/purchase/orders/:id` and
// `/erp/purchase/orders/new`) are owned by the existing PO editor —
// this list page just navigates to them and never mutates rows
// itself except via the soft-delete endpoint already shipped in
// the Stage 6P.2 backend.
//
// Compactness mirrors GlassCategoryMasterPage:
//   • slate-50 header strip + uppercase column titles
//   • 22 px row density
//   • sticky toolbar
//   • ⋯ row-actions menu (Dropdown.Button-style)
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, DatePicker, Dropdown, Input, Modal, Select,
  Space, Spin, Table, Tooltip, message
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, SearchOutlined, MoreOutlined,
  ShoppingCartOutlined, ExclamationCircleOutlined,
  EyeOutlined, EditOutlined, DeleteOutlined,
  StopOutlined, CheckCircleOutlined, SendOutlined
} from '@ant-design/icons';

import { purchaseOrdersApi } from './purchaseApi';
import {
  PO_STATUS_OPTIONS,
  statusLabel,
  canEditPurchaseOrder,
  canDeletePurchaseOrder,
  canPlacePurchaseOrder,
  canCancelPurchaseOrder,
  canClosePurchaseOrder
} from './purchaseHelpers';
import useAuth from '../../../hooks/useAuth';
import './PurchaseOrdersPage.css';

const { RangePicker } = DatePicker;

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = ['25', '50', '100'];
const DATE_FMT = 'DD-MMM-YYYY';
const ISO_FMT  = 'YYYY-MM-DD';

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
}

// INR formatter — backend ships rupee numbers (NUMERIC 14,2).
const INR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});
function fmtMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return INR.format(n);
}

// Status dot colours — a calm signal that doesn't wash the whole cell the
// way a filled tag does, and consistent with the RFQ list.
const STATUS_DOT = {
  DRAFT:              '#94a3b8',
  PLACED:             '#1877f2',
  PARTIALLY_RECEIVED: '#f59e0b',
  RECEIVED:           '#16a34a',
  CLOSED:             '#16a34a',
  CANCELLED:          '#dc2626'
};

function pickField(row, snake, camel) {
  if (!row) return undefined;
  if (camel in row && row[camel] !== undefined && row[camel] !== null) return row[camel];
  if (snake in row && row[snake] !== undefined && row[snake] !== null) return row[snake];
  return undefined;
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function PurchaseOrdersPage() {
  const navigate = useNavigate();
  const { me } = useAuth();

  // Wildcard '*' is the master role; explicit `erp.purchase.orders.*`
  // strings cover admin/manager/staff/viewer per userPermissions.js.
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView   = hasPerm('erp.purchase.orders.view');
  const canCreate = hasPerm('erp.purchase.orders.create');
  const canUpdate = hasPerm('erp.purchase.orders.update');
  const canRemove = hasPerm('erp.purchase.orders.delete');

  // ── Toolbar state ────────────────────────────────────────────
  const [search,    setSearch]    = useState('');
  const [status,    setStatus]    = useState(null);
  const [dateRange, setDateRange] = useState(null);

  // ── Server pagination state ──────────────────────────────────
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total,    setTotal]    = useState(0);

  // ── Data state ───────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId,  setBusyId]  = useState(null);

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = { page, pageSize };
      const needle = (search || '').trim();
      if (needle)  params.search = needle;
      if (status)  params.status = status;
      if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
        params.fromDate = dateRange[0].format
          ? dateRange[0].format(ISO_FMT)
          : new Date(dateRange[0]).toISOString().slice(0, 10);
        params.toDate   = dateRange[1].format
          ? dateRange[1].format(ISO_FMT)
          : new Date(dateRange[1]).toISOString().slice(0, 10);
      }

      const out = await purchaseOrdersApi.list(params);
      // Tolerant unwrap — the api helper already strips one
      // envelope, but list endpoints may still return either
      // { rows, total } or a bare array depending on backend
      // shape. Treat both as authoritative.
      const list = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : [];
      const count = Array.isArray(out) ? list.length
        : Number(out?.total ?? out?.count ?? list.length) || 0;

      setRows(list);
      setTotal(count);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load purchase orders.'),
        key: 'po-list'
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canView, page, pageSize, search, status, dateRange]);

  useEffect(() => { load(); }, [load]);

  // ── Toolbar handlers — any filter change resets page to 1 ────
  const onSearch = (v) => { setSearch(v); setPage(1); };
  const onStatus = (v) => { setStatus(v || null); setPage(1); };
  const onDates  = (v) => { setDateRange(v || null); setPage(1); };
  const onReset  = () => {
    setSearch(''); setStatus(null); setDateRange(null); setPage(1);
  };

  // ── Row navigation ───────────────────────────────────────────
  const openView = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    navigate(`/erp/purchase/orders/${encodeURIComponent(id)}`);
  }, [navigate]);

  const openEdit = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    navigate(`/erp/purchase/orders/${encodeURIComponent(id)}?mode=edit`);
  }, [navigate]);

  const openSourcePr = useCallback((row) => {
    const prId = pickField(row, 'source_pr_id', 'sourcePrId')
      || pickField(row, 'pr_id', 'prId');
    if (!prId) return;
    navigate(`/erp/purchase/requests/${encodeURIComponent(prId)}`);
  }, [navigate]);

  const openNew = useCallback(() => {
    navigate('/erp/purchase/orders/new');
  }, [navigate]);

  // ── Row actions — guarded by canUpdate / canRemove + status ──
  const runPlace = useCallback(async (row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    setBusyId(id);
    try {
      await purchaseOrdersApi.place(id);
      message.success(`PO ${pickField(row, 'po_no', 'poNo') || ''} placed.`);
      await load();
    } catch (e) {
      message.error(errMessage(e, 'Failed to place PO.'));
    } finally {
      setBusyId(null);
    }
  }, [load]);

  const runCancel = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    let reason = '';
    Modal.confirm({
      title: 'Cancel purchase order?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p style={{ marginBottom: 8, fontSize: 12.5 }}>
            This will mark PO <strong>{pickField(row, 'po_no', 'poNo')}</strong>{' '}
            as cancelled. Provide a short reason (optional).
          </p>
          <Input.TextArea
            rows={3}
            maxLength={500}
            placeholder="Reason (optional)"
            onChange={(e) => { reason = e.target.value; }}
          />
        </div>
      ),
      okText: 'Cancel PO',
      okButtonProps: { danger: true },
      cancelText: 'Keep',
      async onOk() {
        setBusyId(id);
        try {
          await purchaseOrdersApi.cancel(id, { reason: reason || undefined });
          message.success('PO cancelled.');
          await load();
        } catch (e) {
          message.error(errMessage(e, 'Failed to cancel PO.'));
          throw e;
        } finally {
          setBusyId(null);
        }
      }
    });
  }, [load]);

  const runClose = useCallback(async (row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    setBusyId(id);
    try {
      await purchaseOrdersApi.close(id);
      message.success('PO closed.');
      await load();
    } catch (e) {
      message.error(errMessage(e, 'Failed to close PO.'));
    } finally {
      setBusyId(null);
    }
  }, [load]);

  const runDelete = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    Modal.confirm({
      title: 'Delete purchase order?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <span style={{ fontSize: 12.5 }}>
          This will soft-delete PO{' '}
          <strong>{pickField(row, 'po_no', 'poNo')}</strong>.
          Only Draft POs can be deleted.
        </span>
      ),
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Keep',
      async onOk() {
        setBusyId(id);
        try {
          await purchaseOrdersApi.remove(id);
          message.success('PO deleted.');
          await load();
        } catch (e) {
          message.error(errMessage(e, 'Failed to delete PO.'));
          throw e;
        } finally {
          setBusyId(null);
        }
      }
    });
  }, [load]);

  // ── Row-action menu builder ──────────────────────────────────
  const buildRowMenu = useCallback((row) => {
    const items = [];
    items.push({
      key: 'view',
      icon: <EyeOutlined />,
      label: 'View'
    });
    if (canUpdate && canEditPurchaseOrder(row)) {
      items.push({
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Edit'
      });
    }
    if (canUpdate && canPlacePurchaseOrder(row)) {
      items.push({
        key: 'place',
        icon: <SendOutlined />,
        label: 'Place'
      });
    }
    if (canUpdate && canClosePurchaseOrder(row)) {
      items.push({
        key: 'close',
        icon: <CheckCircleOutlined />,
        label: 'Close'
      });
    }
    if (canUpdate && canCancelPurchaseOrder(row)) {
      items.push({
        key: 'cancel',
        icon: <StopOutlined />,
        label: 'Cancel',
        danger: true
      });
    }
    if (canRemove && canDeletePurchaseOrder(row)) {
      if (items.length > 1) items.push({ type: 'divider' });
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Delete',
        danger: true
      });
    }
    return items;
  }, [canUpdate, canRemove]);

  const onRowMenu = useCallback((key, row) => {
    switch (key) {
      case 'view':   return openView(row);
      case 'edit':   return openEdit(row);
      case 'place':  return runPlace(row);
      case 'close':  return runClose(row);
      case 'cancel': return runCancel(row);
      case 'delete': return runDelete(row);
      default:       return undefined;
    }
  }, [openView, openEdit, runPlace, runClose, runCancel, runDelete]);

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'PO No',
      dataIndex: 'poNo',
      width:     168,
      render: (_v, row) => {
        const poNo = pickField(row, 'po_no', 'poNo') || '—';
        return (
          <button
            type="button"
            className="po-link"
            onClick={() => openView(row)}
          >
            {poNo}
          </button>
        );
      }
    },
    {
      title:     'PO Date',
      dataIndex: 'poDate',
      width:     120,
      render: (_v, row) => (
        <span className="po-mono">
          {fmtDate(pickField(row, 'po_date', 'poDate'))}
        </span>
      )
    },
    {
      title:     'Supplier',
      dataIndex: 'supplierName',
      render: (_v, row) => {
        const name  = pickField(row, 'supplier_name',  'supplierName')  || '—';
        const gstin = pickField(row, 'supplier_gstin', 'supplierGstin');
        return (
          <div className="po-supplier">
            <span className="po-supplier__name" title={name}>{name}</span>
            {gstin ? <span className="po-supplier__gstin">{gstin}</span> : null}
          </div>
        );
      }
    },
    {
      title:     'Source PR',
      dataIndex: 'prNumberSnapshot',
      width:     150,
      render: (_v, row) => {
        const prNo = pickField(row, 'pr_number_snapshot', 'prNumberSnapshot');
        const prId = pickField(row, 'source_pr_id', 'sourcePrId')
          || pickField(row, 'pr_id', 'prId');
        if (!prNo && !prId) return <span className="po-muted">—</span>;
        // Never surface the raw UUID — when only the id is known, offer a
        // plain link to open the source request instead.
        return (
          <button
            type="button"
            className="po-link po-link--secondary"
            onClick={() => openSourcePr(row)}
          >
            {prNo || 'View PR'}
          </button>
        );
      }
    },
    {
      title:     'Expected Delivery',
      dataIndex: 'expectedDeliveryDate',
      width:     150,
      render: (_v, row) => (
        <span className="po-mono">
          {fmtDate(pickField(row, 'expected_delivery_date', 'expectedDeliveryDate'))}
        </span>
      )
    },
    {
      title:     'Status',
      dataIndex: 'status',
      width:     160,
      render: (v) => {
        if (!v) return <span className="po-muted">—</span>;
        return (
          <span className="po-status">
            <span className="po-sdot" style={{ background: STATUS_DOT[v] || '#94a3b8' }} />
            <span className="po-sname">{statusLabel(v)}</span>
          </span>
        );
      }
    },
    {
      title:     'Grand Total',
      dataIndex: 'grandTotal',
      width:     140,
      align:     'right',
      fixed:     'right',
      render: (_v, row) => (
        <span className="po-money">
          {fmtMoney(pickField(row, 'grand_total', 'grandTotal'))}
        </span>
      )
    },
    {
      title:     '',
      key:       '_actions',
      width:     56,
      fixed:     'right',
      align:     'center',
      render: (_v, row) => {
        const items = buildRowMenu(row);
        const id = pickField(row, 'id', 'id');
        return (
          <Dropdown
            menu={{
              items,
              onClick: ({ key, domEvent }) => {
                if (domEvent && domEvent.stopPropagation) domEvent.stopPropagation();
                onRowMenu(key, row);
              }
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              className="po-actions-btn"
              icon={<MoreOutlined />}
              loading={busyId === id}
              onClick={(e) => e.stopPropagation()}
              aria-label="Row actions"
            />
          </Dropdown>
        );
      }
    }
  ]), [openView, openSourcePr, buildRowMenu, onRowMenu, busyId]);

  // ── Render ───────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="po-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view purchase orders."
          description="Ask your master account to grant erp.purchase.orders.view."
        />
      </div>
    );
  }

  return (
    <div className="po-shell">
      {/* Header strip */}
      <div className="po-headstrip">
        <div className="po-headstrip__lead">
          <span className="po-headstrip__icon"><ShoppingCartOutlined /></span>
          <div className="po-headstrip__text">
            <span className="po-headstrip__title">Purchase Orders</span>
            <span className="po-headstrip__subtitle">
              Track supplier POs, monitor delivery, and drill into line-level
              tax breakdowns. Backend remains the authority for all money math.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="po-stat-chip">
            <strong>{total}</strong> total
          </span>
        </Space>
      </div>

      {/* Sticky toolbar */}
      <div className="po-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search PO no, supplier, GSTIN…"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          onPressEnter={(e) => onSearch(e.target.value)}
          style={{ width: 280 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All statuses"
          value={status || undefined}
          onChange={onStatus}
          options={PO_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          style={{ width: 180 }}
        />
        <RangePicker
          size="small"
          value={dateRange}
          onChange={onDates}
          format={DATE_FMT}
          allowClear
          placeholder={['From', 'To']}
          style={{ width: 240 }}
        />
        <Tooltip title="Clear filters">
          <Button size="small" onClick={onReset}>Clear</Button>
        </Tooltip>
        <div className="po-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
          />
        </Tooltip>
        <Tooltip title={canCreate ? 'Create a new purchase order' : 'You do not have permission to create POs'}>
          <span>
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={openNew}
              disabled={!canCreate}
            >
              New
            </Button>
          </span>
        </Tooltip>
      </div>

      {/* Grid card */}
      <Card className="po-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="po-grid"
            size="small"
            rowKey={(r) => pickField(r, 'id', 'id') || pickField(r, 'po_no', 'poNo')}
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 1024 }}
            onRow={(row) => ({
              onDoubleClick: () => openView(row)
            })}
            pagination={{
              current:           page,
              pageSize,
              total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   total > pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              }
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No purchase orders yet — click New to create your first.'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}
